import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icon from 'react-feather';

import instagramImg from '../../assets/instagram.png';
import linkedinImg  from '../../assets/linkedin.png';
import twitterImg   from '../../assets/twitter.png';
import facebookImg  from '../../assets/facebook.png';
import tiktokImg    from '../../assets/tiktok.png';
import youtubeImg   from '../../assets/youtube.png';
import pinterestImg from '../../assets/pinterest.png';
import threadsImg   from '../../assets/threads.png';
import { getStoredRef } from '../../affiliate';

const LOGO = 'https://cdn2.schedpilot.com/files/logo.png';

const BACKGROUNDS = [
  'https://cdn2.schedpilot.com/files/stefan-stefancik-pzA7QWNCIYg-unsplash-scaled-e1780569793213.jpg',
  'https://cdn2.schedpilot.com/files/annie-spratt-vGgn0xLdy8s-unsplash-scaled-e1780570222771.jpg',
  'https://cdn2.schedpilot.com/files/anna-barsukova-rtpX6teXLsM-unsplash.jpg',
  'https://cdn2.schedpilot.com/files/voyage-pro-nXkuAN6XD40-unsplash.jpg',
  // add more URLs here
];

const TOTAL_STEPS = 4; // steps 1-4 (welcome is step 0)

const GOALS = [
  { id: 'personal',   icon: <Icon.User size={20} />,           label: 'Grow my personal brand'        },
  { id: 'agency',     icon: <Icon.Briefcase size={20} />,      label: "Manage clients' social media"  },
  { id: 'business',   icon: <Icon.TrendingUp size={20} />,     label: 'Market my business'             },
  { id: 'consistent', icon: <Icon.Calendar size={20} />,       label: 'Stay consistent with posting'  },
  { id: 'creator',    icon: <Icon.Video size={20} />,           label: 'Content creator'               },
  { id: 'other',      icon: <Icon.MoreHorizontal size={20} />, label: 'Something else'                },
];

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram',   icon: instagramImg },
  { id: 'linkedin',  label: 'LinkedIn',    icon: linkedinImg  },
  { id: 'twitter',   label: 'X / Twitter', icon: twitterImg   },
  { id: 'facebook',  label: 'Facebook',    icon: facebookImg  },
  { id: 'tiktok',    label: 'TikTok',      icon: tiktokImg    },
  { id: 'youtube',   label: 'YouTube',     icon: youtubeImg   },
  { id: 'pinterest', label: 'Pinterest',   icon: pinterestImg },
  { id: 'threads',   label: 'Threads',     icon: threadsImg   },
];

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ step }) {
  if (step === 0) return null;
  const pct = (step / TOTAL_STEPS) * 100;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7d8fa3', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Step {step} of {TOTAL_STEPS}
        </span>
      </div>
      <div style={{ height: 4, background: '#e8edf2', borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#2fba01', borderRadius: 4, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ── Nav buttons ────────────────────────────────────────────────────────────────

function NavButtons({ onBack, onNext, nextLabel = 'Continue', nextDisabled = false, loading = false }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 32 }}>
      {onBack && (
        <button
          onClick={onBack}
          style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #d1d9e0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
        >
          Back
        </button>
      )}
      <button
        onClick={onNext}
        disabled={nextDisabled || loading}
        style={{
          flex: 2, padding: '11px 0', borderRadius: 10, border: 'none',
          background: nextDisabled ? '#e2e8f0' : '#2fba01',
          color: nextDisabled ? '#94a3b8' : '#fff',
          fontWeight: 700, fontSize: '0.9rem',
          cursor: nextDisabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'background 0.15s',
        }}
      >
        {loading ? (
          <span style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
        ) : (
          <>{nextLabel} {nextLabel !== 'Get started' && nextLabel !== 'Create free account' && <Icon.ArrowRight size={15} />}</>
        )}
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function OnboardingFlow() {
  const navigate = useNavigate();

  const [currentBg, setCurrentBg] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentBg(i => (i + 1) % BACKGROUNDS.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const [step, setStep]               = useState(0);
  const [errors, setErrors]           = useState({});
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError,   setRegisterError]   = useState('');
  const [accountCreated,  setAccountCreated]  = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name:      '',
    email:     '',
    password:  '',
    goal:      '',
    platforms: [],
  });

  const generatePassword = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    const pwd = Array.from({ length: 14 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    set('password', pwd);
    setShowPassword(true);
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const togglePlatform = id =>
    set('platforms', form.platforms.includes(id)
      ? form.platforms.filter(p => p !== id)
      : [...form.platforms, id]);

  const validate = () => {
    if (step === 1) {
      const e = {};
      if (!form.name.trim())                          e.name     = 'Please enter your name';
      if (!form.email.trim())                         e.email    = 'Please enter your email';
      else if (!/\S+@\S+\.\S+/.test(form.email))      e.email    = 'Enter a valid email address';
      if (!form.password)                             e.password = 'Please enter a password';
      else if (form.password.length < 8)              e.password = 'Password must be at least 8 characters';
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    if (step < TOTAL_STEPS) setStep(s => s + 1);
  };

  const back = () => setStep(s => s - 1);

  // ── Registration API call ──────────────────────────────────────────────────

  const handleRegister = async () => {
    setRegisterLoading(true);
    setRegisterError('');
    try {
      const res  = await fetch('https://api.schedpilot.com/wp-json/api/v1/register', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials: 'include' lets the .schedpilot.com referral cookie reach
        // the API, which uses it when the body carries no ref of its own.
        credentials: 'include',
        body: JSON.stringify({
          name:      form.name,
          email:     form.email,
          password:  form.password,
          goal:      form.goal,
          platforms: form.platforms,
          ref:       getStoredRef(),
        }),
      });
      const json = await res.json();

      if (json.code === 200) {
        setAccountCreated(true);
        return;
      }

      if (json.code === 409) {
        setRegisterError('An account with this email already exists. Sign in instead?');
        return;
      }

      setRegisterError(json.message || 'Something went wrong. Please try again.');
    } catch {
      setRegisterError('Network error. Please check your connection and try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  // ── Shell ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', background: '#000' }}>

      {/* ── Background layers (crossfade) ── */}
      {BACKGROUNDS.map((url, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url(${url})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: i === currentBg ? 1 : 0,
          transition: 'opacity 1.5s ease-in-out',
        }} />
      ))}

      {/* ── Top nav ── */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 32px', height: 60,
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        position: 'sticky', top: 0, zIndex: 10,
        isolation: 'isolate',
      }}>
        {/* Logo */}
        <a href="https://schedpilot.com" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
          <img src={LOGO} alt="SchedPilot" style={{ width: 28, height: 28 }} />
          <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>SchedPilot</span>
        </a>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a
            href="https://schedpilot.com"
            style={{ padding: '6px 14px', borderRadius: 8, fontSize: '0.875rem', fontWeight: 500, color: '#475569', textDecoration: 'none' }}
            onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            Home
          </a>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '7px 18px', borderRadius: 8,
              background: '#2fba01', color: '#fff',
              border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600,
            }}
          >
            Login
          </button>
        </div>
      </nav>

      {/* ── Page body ── */}
      <div className="ob-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', position: 'relative', zIndex: 1 }}>

      {/* Card */}
      <div className="ob-card" style={{ background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', width: '100%', maxWidth: 520, padding: '36px 40px' }}>

        {/* ── Account created: check your email ── */}
        {accountCreated && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: '#f0fdf4', border: '2px solid #bbf7d0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <Icon.Mail size={32} color="#2fba01" />
            </div>

            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
              Check your inbox!
            </h2>

            <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 8 }}>
              We sent your login details to
            </p>
            <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem', marginBottom: 24, wordBreak: 'break-all' }}>
              {form.email}
            </p>

            <div style={{ background: '#f8fafc', borderRadius: 12, padding: '16px 20px', marginBottom: 28, textAlign: 'left' }}>
              {[
                { icon: <Icon.CheckCircle size={15} color="#2fba01" />, text: 'Open the email from SchedPilot' },
                { icon: <Icon.CheckCircle size={15} color="#2fba01" />, text: 'Click the activation link to verify your account' },
                { icon: <Icon.CheckCircle size={15} color="#2fba01" />, text: 'Log in with the credentials provided' },
              ].map(({ icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  {icon}
                  <span style={{ fontSize: '0.875rem', color: '#475569' }}>{text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/login')}
              style={{ display: 'block', width: '100%', padding: '13px 0', borderRadius: 12, background: '#2fba01', color: '#fff', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: 'pointer', marginBottom: 12 }}
            >
              Go to sign in
            </button>

            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>
              Didn't receive it? Check your spam folder, or{' '}
              <button
                onClick={() => { setAccountCreated(false); setStep(TOTAL_STEPS); }}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem', padding: 0 }}
              >
                try again
              </button>.
            </p>
          </div>
        )}

        {!accountCreated && (
          <>
            <ProgressBar step={step} />

            {/* ── Step 0: Welcome ── */}
            {step === 0 && (
              <div className="ob-welcome">
                <div className="ob-w-hero" style={{ textAlign: 'center', marginBottom: 32 }}>
                  <div className="ob-w-icon" style={{ width: 64, height: 64, borderRadius: 18, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <Icon.Zap size={28} color="#2fba01" />
                  </div>
                  <h1 className="ob-w-title" style={{ fontSize: '1.7rem', fontWeight: 700, color: '#000', marginBottom: 10 }}>
                    Schedule smarter.<br />Grow faster.
                  </h1>
                  <p className="ob-w-sub" style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>
                    SchedPilot helps you publish to all your social platforms from one place — with AI tools, analytics, and team collaboration built in.
                  </p>

                  {/* Supported platforms — brand-coloured logos */}
                  <div className="ob-w-logos" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 14, marginTop: 22 }}>
                    {[
                      { src: instagramImg, label: 'Instagram' },
                      { src: twitterImg,   label: 'X / Twitter' },
                      { src: linkedinImg,  label: 'LinkedIn' },
                      { src: facebookImg,  label: 'Facebook' },
                      { src: tiktokImg,    label: 'TikTok' },
                      { src: youtubeImg,   label: 'YouTube' },
                      { src: pinterestImg, label: 'Pinterest' },
                      { src: threadsImg,   label: 'Threads' },
                    ].map(({ src, label }) => (
                      <img
                        key={label}
                        className="ob-w-logo"
                        src={src}
                        alt={label}
                        title={label}
                        style={{ width: 30, height: 30, objectFit: 'contain' }}
                      />
                    ))}
                  </div>
                </div>

                <div className="ob-w-features" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 }}>
                  {[
                    { icon: <Icon.Calendar size={16} color="#2fba01" />,  text: 'Schedule posts across all platforms' },
                    { icon: <Icon.Cpu size={16} color="#9333ea" />,       text: 'API and MCP for AI Agents' },
                    { icon: <Icon.BarChart2 size={16} color="#4f46e5" />, text: 'Track performance with built-in analytics' },
                    { icon: <Icon.Zap size={16} color="#f59e0b" />,        text: 'AI-powered post writing & repurposing' },
                    { icon: <Icon.Users size={16} color="#0ea5e9" />,      text: 'Collaborate with your team' },
                  ].map(({ icon, text }) => (
                    <div key={text} className="ob-w-feature" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
                      <div className="ob-w-feature-icon" style={{ width: 30, height: 30, borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {icon}
                      </div>
                      <span style={{ fontSize: '0.875rem', color: '#334155', fontWeight: 500 }}>{text}</span>
                    </div>
                  ))}
                </div>

                <NavButtons onNext={() => setStep(1)} nextLabel="Get started" />
              </div>
            )}

            {/* ── Step 1: Name + Email ── */}
            {step === 1 && (
              <>
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Tell us about you</h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 28 }}>We'll use this to personalise your experience.</p>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>Your name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sarah Johnson"
                    value={form.name}
                    onChange={e => { set('name', e.target.value); setErrors(er => ({ ...er, name: '' })); }}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${errors.name ? '#f87171' : '#d1d9e0'}`, fontSize: '0.9rem', outline: 'none', color: '#0f172a', boxSizing: 'border-box' }}
                  />
                  {errors.name && <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: 4 }}>{errors.name}</p>}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>Email address</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={e => { set('email', e.target.value); setErrors(er => ({ ...er, email: '' })); }}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${errors.email ? '#f87171' : '#d1d9e0'}`, fontSize: '0.9rem', outline: 'none', color: '#0f172a', boxSizing: 'border-box' }}
                  />
                  {errors.email && <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: 4 }}>{errors.email}</p>}
                </div>

                <div style={{ marginBottom: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>Password</label>
                    <button
                      type="button"
                      onClick={generatePassword}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.75rem', color: '#2fba01', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <Icon.RefreshCw size={11} /> Generate
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={form.password}
                      onChange={e => { set('password', e.target.value); setErrors(er => ({ ...er, password: '' })); }}
                      style={{ width: '100%', padding: '11px 42px 11px 14px', borderRadius: 10, border: `1px solid ${errors.password ? '#f87171' : '#d1d9e0'}`, fontSize: '0.9rem', outline: 'none', color: '#0f172a', boxSizing: 'border-box' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                    >
                      {showPassword ? <Icon.EyeOff size={16} /> : <Icon.Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: 4 }}>{errors.password}</p>}
                </div>

                <NavButtons onBack={back} onNext={next} nextDisabled={!form.name.trim() || !form.email.trim() || !form.password} />
              </>
            )}

            {/* ── Step 2: Goal ── */}
            {step === 2 && (
              <>
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>What brings you here?</h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 24 }}>Pick the one that fits you best.</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {GOALS.map(g => {
                    const active = form.goal === g.id;
                    return (
                      <button
                        key={g.id}
                        onClick={() => set('goal', g.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 14,
                          padding: '12px 16px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                          border: `2px solid ${active ? '#2fba01' : '#e2e8f0'}`,
                          background: active ? '#f0fdf4' : '#fff',
                          color: '#0f172a', fontWeight: active ? 600 : 400,
                          transition: 'all 0.15s', fontSize: '0.9rem',
                        }}
                      >
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: active ? '#dcfce7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: active ? '#16a34a' : '#64748b' }}>
                          {g.icon}
                        </div>
                        {g.label}
                        {active && <Icon.Check size={16} color="#2fba01" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>

                <NavButtons onBack={back} onNext={next} nextDisabled={!form.goal} />
              </>
            )}

            {/* ── Step 3: Platforms ── */}
            {step === 3 && (
              <>
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Which platforms do you use?</h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 24 }}>Select all that apply — you can connect them after signing up.</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {PLATFORMS.map(p => {
                    const active = form.platforms.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => togglePlatform(p.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '12px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                          border: `2px solid ${active ? '#2fba01' : '#e2e8f0'}`,
                          background: active ? '#f0fdf4' : '#fff',
                          color: active ? '#0f172a' : '#475569',
                          fontWeight: active ? 600 : 400,
                          transition: 'all 0.15s', fontSize: '0.85rem',
                        }}
                      >
                        <img src={p.icon} alt={p.label} style={{ width: 22, height: 22, borderRadius: 5, flexShrink: 0 }} />
                        {p.label}
                        {active && <Icon.Check size={13} color="#2fba01" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>

                {/* step 3 just advances — no API call here */}
                <NavButtons onBack={back} onNext={next} nextLabel="Continue" />
              </>
            )}

            {/* ── Step 4: Summary + Create account ── */}
            {step === TOTAL_STEPS && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#f0fdf4', border: '2px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <Icon.CheckCircle size={34} color="#2fba01" />
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                  You're all set, {form.name.split(' ')[0]}!
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: 24 }}>
                  Create your free account to start scheduling posts, connecting your platforms, and growing your audience.
                </p>

                {/* Summary chips */}
                {(form.goal || form.platforms.length > 0) && (
                  <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 16px', marginBottom: 24, textAlign: 'left' }}>
                    {form.goal && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: form.platforms.length > 0 ? 8 : 0 }}>
                        <Icon.Target size={13} color="#7d8fa3" />
                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          Goal: <strong style={{ color: '#334155' }}>{GOALS.find(g => g.id === form.goal)?.label}</strong>
                        </span>
                      </div>
                    )}
                    {form.platforms.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Icon.Globe size={13} color="#7d8fa3" />
                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Platforms:</span>
                        {form.platforms.map(pid => {
                          const p = PLATFORMS.find(x => x.id === pid);
                          return p ? <img key={pid} src={p.icon} alt={p.label} title={p.label} style={{ width: 18, height: 18, borderRadius: 4 }} /> : null;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Error message */}
                {registerError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}>
                    <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>
                      {registerError}
                      {registerError.includes('already exists') && (
                        <> <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem', padding: 0 }}>Sign in →</button></>
                      )}
                    </p>
                  </div>
                )}

                {/* Create account button */}
                <button
                  onClick={handleRegister}
                  disabled={registerLoading}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', padding: '13px 0', borderRadius: 12,
                    background: registerLoading ? '#a3d977' : '#2fba01',
                    color: '#fff', fontWeight: 700, fontSize: '1rem',
                    border: 'none', cursor: registerLoading ? 'default' : 'pointer',
                    marginBottom: 12, transition: 'background 0.15s',
                  }}
                >
                  {registerLoading
                    ? <><span style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> Creating your account…</>
                    : 'Create free account'
                  }
                </button>

                <button
                  onClick={() => navigate('/login')}
                  style={{ display: 'block', width: '100%', padding: '11px 0', borderRadius: 12, background: '#fff', border: '1px solid #d1d9e0', color: '#475569', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', textAlign: 'center' }}
                >
                  Already have an account? Sign in
                </button>
              </div>
            )}
          </>
        )}

      </div>

      {/* Footer */}
      <p className="ob-card-footer" style={{ marginTop: 20, fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', width: '100%', maxWidth: 520 }}>
        By continuing you agree to SchedPilot's{' '}
        <a href="https://schedpilot.com/terms" style={{ color: '#64748b' }}>Terms</a>
        {' '}and{' '}
        <a href="https://schedpilot.com/privacy" style={{ color: '#64748b' }}>Privacy Policy</a>
      </p>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus { border-color: #2fba01 !important; box-shadow: 0 0 0 3px rgba(47,186,1,0.12); }
        @media (min-width: 769px) { .ob-card, .ob-card-footer { margin-left: 84px; } }

        /* Welcome screen (step 0) — keep everything above the fold on mobile */
        @media (max-width: 768px) {
          .ob-body { justify-content: flex-start !important; padding: 14px 14px !important; }
          .ob-card { padding: 22px 18px !important; }
          .ob-welcome .ob-w-hero { margin-bottom: 16px !important; }
          .ob-welcome .ob-w-icon { width: 46px !important; height: 46px !important; border-radius: 13px !important; margin: 0 auto 12px !important; }
          .ob-welcome .ob-w-title { font-size: 1.35rem !important; margin-bottom: 6px !important; }
          .ob-welcome .ob-w-sub { font-size: 0.82rem !important; line-height: 1.45 !important; }
          .ob-welcome .ob-w-logos { gap: 10px !important; margin-top: 14px !important; }
          .ob-welcome .ob-w-logo { width: 26px !important; height: 26px !important; }
          .ob-welcome .ob-w-features { gap: 7px !important; }
          .ob-welcome .ob-w-feature { padding: 7px 12px !important; }
          .ob-welcome .ob-w-feature-icon { width: 26px !important; height: 26px !important; }
          .ob-welcome > div:last-child { margin-top: 18px !important; }
        }
      `}</style>

      </div>{/* end page body */}
    </div>
  );
}

export default OnboardingFlow;
